import Flags from "common/constants/flags";

const RELOAD_TIMESTAMP_KEY = "chunk-reload-timestamp";
const DEFAULT_COOLDOWN_MS = 60_000;
const CLIENT_RELOAD_DELAY_MS = 1200;
const CLIENT_RELOAD_BANNER_ID = "client-reload-banner";
const CLIENT_RELOAD_PROMPT_ID = "client-reload-prompt";
let chunkReloadPending: boolean = false;

// Reloads the current page. Indirection through this module lets tests assert
// the reload was triggered without needing to redefine the read-only
// window.location.reload property in jsdom.
export function reloadPage(): void {
  window.location.reload();
}

function showClientReloadBanner(): void {
  if (typeof document === "undefined") {
    return;
  }
  const existingBanner = document.getElementById(CLIENT_RELOAD_BANNER_ID);
  if (existingBanner) {
    return;
  }

  const banner = document.createElement("div");
  banner.id = CLIENT_RELOAD_BANNER_ID;
  banner.textContent = "A new deployment is available. Reloading now...";
  banner.setAttribute("role", "status");
  banner.style.cssText =
    "position:fixed;top:12px;left:50%;transform:translateX(-50%);z-index:2147483647;" +
    "padding:10px 14px;border-radius:8px;background:#121825;color:#f8fafc;" +
    "font:600 13px/1.4 ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto,sans-serif;" +
    "box-shadow:0 8px 24px rgba(0,0,0,0.28);";
  document.body.appendChild(banner);
}

function showPersistentClientReloadPrompt(): void {
  if (typeof document === "undefined") {
    return;
  }

  const existingPrompt = document.getElementById(CLIENT_RELOAD_PROMPT_ID);
  if (existingPrompt) {
    return;
  }

  const prompt = document.createElement("div");
  prompt.id = CLIENT_RELOAD_PROMPT_ID;
  prompt.setAttribute("role", "status");
  prompt.setAttribute("aria-live", "polite");
  prompt.style.cssText =
    "position:fixed;top:12px;left:50%;transform:translateX(-50%);z-index:2147483647;" +
    "display:flex;align-items:center;gap:12px;max-width:min(420px,calc(100vw - 24px));" +
    "padding:10px 12px;border-radius:16.5px;border:1px solid #47b279;background:#dcf0e6;color:rgba(0,0,0,0.95);" +
    "font:600 13px/1.4 ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto,sans-serif;" +
    "box-shadow:0 8px 24px rgba(0,0,0,0.28);";

  const message = document.createElement("span");
  message.textContent = "A new version of Higharc was released! Reload to get the latest changes.";
  message.style.cssText = "min-width:0;";

  const reloadButton = document.createElement("button");
  reloadButton.type = "button";
  reloadButton.textContent = "Reload";
  reloadButton.style.cssText =
    "flex-shrink:0;cursor:pointer;border:0;border-radius:6px;padding:6px 10px;" +
    "background:#47b279;color:#ffffff;font:700 12px/1 ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto,sans-serif;";
  reloadButton.addEventListener("click", () => {
    reloadPage();
  });

  prompt.append(message, reloadButton);
  document.body.appendChild(prompt);
}

export function reloadPageWithNotification(reason: string): void {
  if (typeof window === "undefined" || !Flags.ENABLE_CLIENT_RELOAD_ON_DEPLOYMENT_CHANGE) {
    return;
  }
  console.warn(`[client-reload] ${reason}. Reloading to fetch the latest client bundle.`);
  showClientReloadBanner();
  window.setTimeout(() => {
    chunkReloadPending = false;
    reloadPage();
  }, CLIENT_RELOAD_DELAY_MS);
  chunkReloadPending = true;
}

/** Returns whether an automatic reload is scheduled in this page. */
export function hasPendingChunkReload(): boolean {
  return chunkReloadPending;
}

export function showPersistentReloadNotification(reason: string): void {
  if (typeof window === "undefined" || !Flags.ENABLE_CLIENT_RELOAD_ON_DEPLOYMENT_CHANGE) {
    return;
  }

  console.warn(`[client-reload] ${reason}. Waiting for user-triggered reload to fetch the latest client bundle.`);
  showPersistentClientReloadPrompt();
}

// Records a reload attempt and returns true if a reload should proceed.
// Prevents tight reload loops when a chunk load error is reproducible (e.g.
// the deployed bundle itself has a runtime error), while still allowing the
// recovery path to fire again later in the session if a *new* deploy lands.
export function shouldAttemptChunkReload(cooldownMs = DEFAULT_COOLDOWN_MS): boolean {
  if (typeof window === "undefined" || !Flags.ENABLE_CLIENT_RELOAD_ON_DEPLOYMENT_CHANGE) {
    return false;
  }
  // sessionStorage access can throw SecurityError in restricted contexts
  // (sandboxed iframes, certain privacy modes). Any throw from here would
  // escape componentDidCatch and bypass the Studio recovery UI, so we treat
  // storage failure as "first attempt" and let the reload proceed unguarded.
  try {
    const last = Number(sessionStorage.getItem(RELOAD_TIMESTAMP_KEY) ?? 0);
    if (Number.isFinite(last) && Date.now() - last < cooldownMs) {
      return false;
    }
    sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(Date.now()));
  } catch {
    // fall through and allow the reload
  }
  return true;
}
